(async () => {
  if (!customElements.get('product-media')) {
    const { default: Swiper, Navigation, Mousewheel } = await import('swiper');
    const { Fancybox } = await import('@fancyapps/ui');

    Swiper.use([Navigation, Mousewheel]);

    class ProductMedia extends HTMLElement {
      constructor() {
        super();
        this.mediaContainer = this.closest('.product-media') || this;
        this.fancyboxOptions = {
          animated: false,
          showClass: false,
          mainClass: 'image-zoom',
          Image: {
            Panzoom: {
              maxScale: function () {
                return 2.5;
              },
            },
          },
          Carousel: {
            Navigation: {
              nextTpl: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path fill-rule="evenodd" clip-rule="evenodd" d="M16.3718 11L13.5432 8.17142L14.9574 6.7572L20.2002 12L14.9574 17.2427L13.5432 15.8285L16.3718 13H4.00041V11H16.3718Z" fill="currentColor"/>
            </svg>`,
              prevTpl: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path fill-rule="evenodd" clip-rule="evenodd" d="M7.62823 13L10.4568 15.8286L9.04256 17.2428L3.7998 12L9.04256 6.7573L10.4568 8.17151L7.62823 11H19.9996V13H7.62823Z" fill="currentColor"/>
            </svg>`,
            },
          },
          Toolbar: {
            items: {
              close: {
                type: 'button',
                label: 'CLOSE',
                class: 'fancybox__button--close',
                html: `
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path fill-rule="evenodd" clip-rule="evenodd" d="M7.75744 6.34315L6.34323 7.75736L10.5859 12L6.34323 16.2426L7.75744 17.6569L12.0001 13.4142L16.2427 17.6569L17.6569 16.2426L13.4143 12L17.6569 7.75736L16.2427 6.34315L12.0001 10.5858L7.75744 6.34315Z" fill="currentColor"/>
                  </svg>`,
              },
            },
          },
        };

        this.swiper();
        this.fancybox();

        this.onVariantChange = (e) => {
          if (Fancybox.getInstance()) {
            Fancybox.close(true);
          }
          const variantId = e?.detail?.variant?.id;
          if (variantId) {
            this.updateFancyboxInclude(variantId);
            this.updateRulerHeight(variantId);
          }
          this.bindFancybox();
        };
        document.addEventListener('variant-change', this.onVariantChange);
      }

      disconnectedCallback() {
        document.removeEventListener('variant-change', this.onVariantChange);
      }

      swiper() {
        if (typeof Swiper == 'undefined') {
          return false;
        }

        this.swiperElement = this.querySelector('[data-product-media-swiper]');

        this.swiperOptions = {
          loop: true,
          slidesPerView: 'auto',
          freeMode: false,
          spaceBetween: '8px',
          mousewheel: {
            enabled: true,
            releaseOnEdges: true,
          },
        };

        this.swiperInstance = new Swiper(this.swiperElement, this.swiperOptions);

        if (Shopify.designMode && this.swiperInstance) {
          window.addEventListener('shopify:section:load', () => {
            this.swiperInstance.update();
          });
        }
      }

      fancybox() {
        const variantId = this.getCurrentVariantId();
        if (variantId) {
          this.updateFancyboxInclude(variantId);
          this.updateRulerHeight(variantId);
        }
        this.bindFancybox();
      }

      bindFancybox() {
        this.applyFancyboxGroups();
        Fancybox.unbind(this.mediaContainer);
        Fancybox.bind(this.mediaContainer, '[data-fancybox]', this.fancyboxOptions);
      }

      isExcludedFromFancybox(el) {
        return el.closest(
          '.product-media__variant-image.hidden, .product-media__variant-slide.hidden, .swiper-slide.hidden'
        );
      }

      applyFancyboxGroups() {
        this.mediaContainer
          .querySelectorAll('[data-fancybox-include], [data-fancybox-group]')
          .forEach((el) => {
            const shouldInclude = el.dataset.fancyboxInclude === 'true';

            if (shouldInclude) {
              if (!el.hasAttribute('data-fancybox') && el.dataset.fancyboxGroup) {
                el.setAttribute('data-fancybox', el.dataset.fancyboxGroup);
              }
            } else if (el.hasAttribute('data-fancybox')) {
              el.dataset.fancyboxGroup = el.getAttribute('data-fancybox');
              el.removeAttribute('data-fancybox');
            }
          });
      }

      getCurrentVariantId() {
        const variantInput = document.querySelector('product-form input[name="id"]');
        if (variantInput?.value) {
          return parseInt(variantInput.value, 10);
        }

        return null;
      }

      getVariantMediaConfig() {
        const configElement = this.mediaContainer.querySelector('[data-variant-media-config]');
        if (!configElement) return null;

        try {
          return JSON.parse(configElement.textContent);
        } catch {
          return null;
        }
      }

      updateRulerHeight(variantId) {
        const config = this.getVariantMediaConfig();
        if (!config?.[variantId]) return;

        const height = config[variantId].height;

        this.mediaContainer.querySelectorAll('[data-ruler-height]').forEach((badge) => {
          const label = badge.querySelector('span');

          if (!height) {
            badge.classList.add('hidden');
            if (label) label.textContent = '';
            return;
          }

          badge.classList.remove('hidden');
          if (label) label.textContent = height;
        });
      }

      updateFancyboxInclude(variantId) {
        const numericId = parseInt(variantId, 10);

        this.mediaContainer.querySelectorAll('[data-fancybox-variant]').forEach((el) => {
          const included =
            parseInt(el.dataset.fancyboxVariant, 10) === numericId &&
            !this.isExcludedFromFancybox(el);
          el.dataset.fancyboxInclude = included ? 'true' : 'false';
        });

        this.mediaContainer.querySelectorAll('[data-fancybox-variant-ids]').forEach((el) => {
          let included = true;

          try {
            const variantIds = JSON.parse(el.dataset.fancyboxVariantIds || '[]').map((id) =>
              parseInt(id, 10)
            );
            included = variantIds.length === 0 || variantIds.includes(numericId);
          } catch {
            included = true;
          }

          if (this.isExcludedFromFancybox(el)) {
            included = false;
          }

          el.dataset.fancyboxInclude = included ? 'true' : 'false';
        });
      }
    }

    window.ProductMedia = ProductMedia;

    customElements.define('product-media', ProductMedia);
  }
})();
